import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, TrendingUp, Wallet, Calculator, Settings, X, ChevronDown, ChevronUp, Plus, ArrowUpCircle, ArrowDownCircle, Search, BarChart3, AlertTriangle, Bot, Activity, ExternalLink } from 'lucide-react';
import StatCard from './StatCard';
import PnLCalculator from './PnLCalculator';
import SettingsPanel from './SettingsPanel';
import PriceTicker from './PriceTicker';
import GannCalculator from './GannCalculator';
import HLTrendBotPanel from './HLTrendBotPanel';
// GannV3Bot import REMOVED 2026-05-24 — paper period expired 2026-05-18, verdict REJECT.
// File retained at components/GannV3Bot.jsx (orphaned) for rollback safety.
// ACCESS: Admin only (Stage 1) | ENABLED_FOR: admin | TESTED: pending
import TsmomR36Bot from './TsmomR36Bot';
import Vol01Panel from './Vol01Panel';
import '../styles/Dashboard.css';

// Binance API for fetching current prices
const BINANCE_API = 'https://api.binance.com/api/v3';
const DEFAULT_TAB = 'positions';
const ACTIVE_TABS = ['positions', 'calculator', 'gann', 'hl-trend-bot', 'r36', 'vol01', 'settings'];
const ADMIN_ONLY_TABS = ['hl-trend-bot', 'r36', 'vol01'];

function Dashboard({ userRole = 'admin', currentUser = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(7000);
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL hash first, then localStorage
    const hash = window.location.hash.replace('#', '');
    if (ACTIVE_TABS.includes(hash)) {
      return hash;
    }
    if (hash) {
      return DEFAULT_TAB;
    }
    const savedTab = localStorage.getItem('dashboard_active_tab');
    return ACTIVE_TABS.includes(savedTab) ? savedTab : DEFAULT_TAB;
  });
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [expandedPosition, setExpandedPosition] = useState(null);
  const [showNewPosition, setShowNewPosition] = useState(false);
  const [balances, setBalances] = useState({ coindcx: null, hyperliquid: null });
  const [symbols, setSymbols] = useState({ coindcx: [], hyperliquid: [] });
  const [symbolSearch, setSymbolSearch] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmCooldown, setConfirmCooldown] = useState(0);
  const [selectedExchanges, setSelectedExchanges] = useState(['hyperliquid', 'hyperliquid_2', 'lighter', 'aster', 'blofin']);
  const [multiBalances, setMultiBalances] = useState(null);
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [maxLeverage, setMaxLeverage] = useState(25);
  // eslint-disable-next-line no-unused-vars
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loadingPrice, setLoadingPrice] = useState(false);
  const symbolDropdownRef = useRef(null);
  
  const [newOrder, setNewOrder] = useState({
    exchange: 'coindcx',
    symbol: '',
    side: 'long',
    orderType: 'market',
    margin: '',
    marginPercent: 0,
    price: '',
    leverage: '10'
  });

  // Get available margin for selected exchange
  const getAvailableMargin = useCallback(() => {
    const bal = balances[newOrder.exchange];
    if (!bal) return 0;
    
    if (newOrder.exchange === 'coindcx') {
      // CoinDCX uses INR or USDT
      return bal?.USDT?.available || bal?.INR?.available || 0;
    } else {
      // Hyperliquid uses USDC
      return bal?.USDC?.available || bal?.available || 0;
    }
  }, [balances, newOrder.exchange]);



  // Calculate position size from margin and leverage
  const calculatePositionSize = useCallback(() => {
    const margin = parseFloat(newOrder.margin) || 0;
    const leverage = parseInt(newOrder.leverage) || 1;
    return margin * leverage;
  }, [newOrder.margin, newOrder.leverage]);

  // Calculate quantity (contracts) based on position size and price
  const calculateQuantity = useCallback(() => {
    if (!currentPrice) return 0;
    const positionSize = calculatePositionSize();
    return positionSize / currentPrice;
  }, [currentPrice, calculatePositionSize]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/dashboard`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
          return;
        }
        throw new Error('Failed to fetch');
      }

      const dashData = await response.json();
      if (!dashData.success) {
        throw new Error('API returned unsuccessful response');
      }

      setData(dashData);
      setLastUpdate(new Date());

      if (dashData.update_frequency) {
        const match = dashData.update_frequency.match(/(\d+)\s*second/i);
        if (match) {
          setRefreshInterval(parseInt(match[1]) * 1000);
        }
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch balance for an exchange
  const fetchBalance = useCallback(async (exchange) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/balance?exchange=${exchange}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const result = await response.json();
        // Include trading_enabled flag in the balance data
        setBalances(prev => ({ 
          ...prev, 
          [exchange]: {
            ...result.balances,
            trading_enabled: result.trading_enabled
          }
        }));
      }
    } catch (err) {
      console.error(`Balance fetch error for ${exchange}:`, err);
    }
  }, []);

  // Fetch current price from Binance
  const fetchCurrentPrice = useCallback(async (symbol) => {
    if (!symbol) {
      setCurrentPrice(null);
      return;
    }
    
    setLoadingPrice(true);
    try {
      // Convert symbol to Binance format
      let binanceSymbol = symbol;
      if (symbol.startsWith('B-')) {
        binanceSymbol = symbol.replace('B-', '').replace('_', '');
      } else {
        binanceSymbol = symbol + 'USDT';
      }
      
      const response = await fetch(`${BINANCE_API}/ticker/price?symbol=${binanceSymbol}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentPrice(parseFloat(data.price));
      }
    } catch (err) {
      console.error('Price fetch error:', err);
    } finally {
      setLoadingPrice(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const interval = setInterval(fetchDashboard, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchDashboard, refreshInterval]);

  // Persist active tab to localStorage and URL hash
  useEffect(() => {
    localStorage.setItem('dashboard_active_tab', activeTab);
    window.location.hash = activeTab;
  }, [activeTab]);

  useEffect(() => {
    if (!ACTIVE_TABS.includes(activeTab)) {
      setActiveTab(DEFAULT_TAB);
      return;
    }
    if (userRole !== 'admin' && ADMIN_ONLY_TABS.includes(activeTab)) {
      setActiveTab(DEFAULT_TAB);
    }
  }, [activeTab, userRole]);

  // Fetch symbols for an exchange
  const fetchSymbols = useCallback(async (exchange) => {
    if (symbols[exchange]?.length > 0) return;
    setLoadingSymbols(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/symbols?exchange=${exchange}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const result = await response.json();
        setSymbols(prev => ({ ...prev, [exchange]: result.symbols || [] }));
      }
    } catch (err) {
      console.error(`Symbols fetch error for ${exchange}:`, err);
    } finally {
      setLoadingSymbols(false);
    }
  }, [symbols]);

  // Fetch instrument details (max leverage) for selected symbol
  const fetchInstrumentDetails = useCallback(async (exchange, symbol) => {
    if (!symbol) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/instrument?exchange=${exchange}&symbol=${encodeURIComponent(symbol)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.max_leverage) {
          setMaxLeverage(result.max_leverage);
          if (parseInt(newOrder.leverage) > result.max_leverage) {
            setNewOrder(prev => ({ ...prev, leverage: String(result.max_leverage) }));
          }
        }
      }
    } catch (err) {
      console.error('Instrument fetch error:', err);
    }
  }, [newOrder.leverage]);

  // Fetch balance and symbols when opening panel
  useEffect(() => {
    if (showNewPosition) {
      fetchBalance(newOrder.exchange);
      fetchSymbols(newOrder.exchange);
    }
  }, [showNewPosition, newOrder.exchange, fetchBalance, fetchSymbols]);

  // Fetch instrument details and price when symbol changes
  useEffect(() => {
    if (newOrder.symbol) {
      fetchInstrumentDetails(newOrder.exchange, newOrder.symbol);
      fetchCurrentPrice(newOrder.symbol);
    } else {
      setMaxLeverage(25);
      setCurrentPrice(null);
    }
  }, [newOrder.symbol, newOrder.exchange, fetchInstrumentDetails, fetchCurrentPrice]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(e.target)) {
        setShowSymbolDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const filteredSymbols = symbols[newOrder.exchange]?.filter(sym => {
    const symbolName = typeof sym === 'string' ? sym : sym.symbol;
    return symbolName.toLowerCase().includes(symbolSearch.toLowerCase());
  }) || [];

  // eslint-disable-next-line no-unused-vars
  const handleOpenPosition = async (e) => {
    e.preventDefault();
    setActionLoading('new_position');
    setMessage(null);

    const quantity = calculateQuantity();
    if (quantity <= 0) {
      setMessage({ type: 'error', text: 'Invalid position size. Check margin and symbol.' });
      setActionLoading(null);
      return;
    }

    try {
      const payload = {
        exchange: newOrder.exchange,
        side: newOrder.side === 'long' ? 'buy' : 'sell',
        quantity: quantity,
        order_type: newOrder.orderType === 'market' ? 'market_order' : 'limit_order',
        leverage: parseInt(newOrder.leverage)
      };

      if (newOrder.exchange === 'coindcx') {
        payload.pair = newOrder.symbol;
        payload.margin_currency = 'USDT';
        payload.time_in_force = newOrder.orderType === 'market' ? 'immediate_or_cancel' : 'good_till_cancel';
        if (newOrder.orderType === 'limit') {
          payload.price = parseFloat(newOrder.price);
        }
      } else if (newOrder.exchange === 'hyperliquid') {
        payload.symbol = newOrder.symbol;
        payload.order_type = newOrder.orderType;
        if (newOrder.orderType === 'limit') {
          payload.price = parseFloat(newOrder.price);
          payload.time_in_force = 'Gtc';
        }
      } else {
        // CCXT-based: lighter, aster, blofin, edgex
        payload.symbol = newOrder.symbol;
        payload.order_type = newOrder.orderType;
        if (newOrder.orderType === 'limit') {
          payload.price = parseFloat(newOrder.price);
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/orders/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();

      if (result.success) {
        setMessage({ type: 'success', text: `Opened ${newOrder.side.toUpperCase()} ${newOrder.symbol}` });
        setShowNewPosition(false);
        setNewOrder({ ...newOrder, symbol: '', margin: '', marginPercent: 0, price: '' });
        fetchDashboard();
        fetchBalance(newOrder.exchange);
      } else {
        setMessage({ type: 'error', text: result.detail || result.message || 'Failed to open position' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  // Close position — shows confirmation modal first
  const requestClosePosition = (position, exchange, percent) => {
    const quantity = (position.size * percent) / 100;
    const cooldown = percent === 100 ? 3 : 0;
    setConfirmCooldown(cooldown);
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setConfirmCooldown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    setConfirmModal({
      type: 'close',
      position,
      exchange,
      percent,
      quantity,
      side: position.side === 'long' ? 'sell' : 'buy',
    });
  };

  const executeClosePosition = async () => {
    if (!confirmModal) return;
    const { position, exchange, percent, quantity, side } = confirmModal;
    setConfirmModal(null);

    const posKey = `${exchange}_${position.symbol}_${percent}`;
    setActionLoading(posKey);
    setMessage(null);

    try {
      let payload = {
        exchange,
        side,
        quantity,
        order_type: 'market_order',
        reduce_only: true
      };

      if (exchange === 'coindcx') {
        payload.pair = position.symbol;
        payload.margin_currency = position.margin_currency;
        payload.time_in_force = 'immediate_or_cancel';
      } else if (exchange === 'hyperliquid') {
        payload.symbol = position.symbol;
        payload.order_type = 'market';
        payload.time_in_force = 'Ioc';
        // Route to the wallet that owns this position (Primary or Trading)
        payload.wallet = position.account_name || 'Primary';
      } else {
        payload.symbol = position.symbol_raw || position.symbol;
        payload.order_type = 'market';
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/orders/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();

      if (result.success) {
        setMessage({ type: 'success', text: `Closed ${percent}% of ${position.symbol}` });
        fetchDashboard();
        setExpandedPosition(null);
      } else {
        setMessage({ type: 'error', text: result.message || 'Failed to close position' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    return Number(num).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  if (loading && activeTab === 'positions') {
    return (
      <div className="loading">
        <RefreshCw size={40} className="spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const tabs = [
    { id: 'positions', label: 'Positions', icon: Wallet },
    { id: 'calculator', label: 'Calculator', icon: Calculator },
    { id: 'gann', label: 'Gann', icon: BarChart3 },
    { id: 'hl-trend-bot', label: 'HL Trend v19', icon: Bot },   // ACCESS: Admin only (Stage 1)
    // Gann V3 tab REMOVED 2026-05-24 — paper period expired, verdict REJECT.
    { id: 'r36', label: 'R3.6 TSMOM', icon: Bot },   // ACCESS: Admin only (Stage 1)
    { id: 'vol01', label: 'VOL01', icon: Activity }, // ACCESS: Admin only (Stage 1)
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // eslint-disable-next-line no-unused-vars
  const availableMargin = getAvailableMargin();
  // eslint-disable-next-line no-unused-vars
  const positionSize = calculatePositionSize();
  // eslint-disable-next-line no-unused-vars
  const marginCurrency = 'USDT';

  const PositionRow = ({ position, exchange }) => {
    const isProfit = position.pnl >= 0;
    const isLong = position.side === 'long';
    const posKey = `${exchange}_${position.symbol}`;
    const isExpanded = expandedPosition === posKey;

    return (
      <div className={`position-item ${isExpanded ? 'expanded' : ''} ${isProfit ? 'pos-profit' : 'pos-loss'}`}>
        <div className="position-main">
          <div className="position-left">
            <span className="position-symbol">{position.symbol}</span>
            <span className={`position-side ${isLong ? 'long' : 'short'}`}>
              {position.side.toUpperCase()}
            </span>
            <span className="position-leverage">{formatNumber(position.leverage, 1)}x</span>
            <span className="position-prices">
              {formatNumber(position.entry_price)} → {formatNumber(position.current_price)}
            </span>
          </div>
          <div className="position-right">
            <span className={`position-pnl ${isProfit ? 'profit' : 'loss'}`}>
              {isProfit ? '+' : ''}{formatNumber(position.pnl)} {position.margin_currency}
            </span>
            <span className={`position-roe ${isProfit ? 'profit' : 'loss'}`}>
              {isProfit ? '+' : ''}{formatNumber(position.pnl_percentage * 100)}%
            </span>
            <button
              className="quick-close-btn"
              title="Close position"
              onClick={() => requestClosePosition(position, exchange, 100)}
            >
              <X size={14} />
            </button>
            <button
              className="expand-btn"
              onClick={() => setExpandedPosition(isExpanded ? null : posKey)}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="position-details">
            <div className="position-stats">
              <div className="stat">
                <span className="stat-label">Size</span>
                <span className="stat-value">{formatNumber(position.size, 4)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Entry</span>
                <span className="stat-value">${formatNumber(position.entry_price)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Mark</span>
                <span className="stat-value">${formatNumber(position.current_price)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Liq. Price</span>
                <span className="stat-value liq">${formatNumber(position.liquidation_price)}</span>
              </div>
            </div>
            <div className="position-actions">
              {[25, 50, 100].map(pct => {
                const btnKey = `${exchange}_${position.symbol}_${pct}`;
                return (
                  <button
                    key={pct}
                    className={`close-btn close-${pct}`}
                    onClick={() => requestClosePosition(position, exchange, pct)}
                    disabled={actionLoading === btnKey}
                  >
                    {actionLoading === btnKey ? '...' : `Close ${pct}%`}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard">
      <PriceTicker />
      <div className="dashboard-tabs">
        {tabs
          .filter(tab => {
            return !(ADMIN_ONLY_TABS.includes(tab.id) && userRole !== 'admin');
          })
          .map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        {/* ADMIN ONLY - Stage 1: external link to Market Board app, NOT a tab.
            Deliberately outside the tabs array so it never enters
            ACTIVE_TABS / hash / localStorage tab-state machinery. */}
        {userRole === 'admin' && (
          <a className="tab-btn tab-btn-link" href="https://board.spiralarc.com">
            <ExternalLink size={16} />
            <span>Market Board</span>
          </a>
        )}
      </div>

      {message && (
        <div className={`toast-message ${message.type}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}><X size={14} /></button>
        </div>
      )}

      {activeTab === 'positions' && data && (() => {
        // FIX: use backend-normalized total_pnl_usdt so CoinDCX INR positions are
        // correctly included (they used to be dropped from this total).
        const usdtPnl = (typeof data.total_pnl_usdt === 'number')
          ? data.total_pnl_usdt
          : ((data.hyperliquid?.pnl_usdt || data.hyperliquid?.pnl || 0)
             + (data.lighter?.pnl_usdt || data.lighter?.pnl || 0)
             + (data.aster?.pnl_usdt || data.aster?.pnl || 0)
             + (data.blofin?.pnl_usdt || data.blofin?.pnl || 0)
             + (data.coindcx?.pnl_usdt || 0));
        const activeExchanges = ['coindcx', 'hyperliquid', 'lighter', 'aster', 'blofin']
          .filter(e => data[e]?.count > 0).length;
        return (
        <>
          <div className="stats-grid stats-grid-4">
            <StatCard
              label="Positions"
              value={data.total_positions}
              icon={<Wallet size={20} />}
              gradient="primary"
            />
            <StatCard
              label="P&L (INR)"
              value={`${data.total_pnl >= 0 ? '+' : ''}₹${formatNumber(data.total_pnl)}`}
              icon={<TrendingUp size={20} />}
              gradient={data.total_pnl >= 0 ? 'success' : 'danger'}
            />
            <StatCard
              label="P&L (USDT)"
              value={`${usdtPnl >= 0 ? '+' : ''}$${formatNumber(usdtPnl)}`}
              icon={<TrendingUp size={20} />}
              gradient={usdtPnl >= 0 ? 'success' : 'danger'}
            />
            <StatCard
              label="Exchanges"
              value={activeExchanges}
              icon={<BarChart3 size={20} />}
              gradient="primary"
            />
          </div>

          {/* Multi-Exchange Trade Panel */}
          <div className="new-position-section">
            <button
              className={`new-position-toggle ${showNewPosition ? 'active' : ''}`}
              onClick={() => {
                setShowNewPosition(!showNewPosition);
                if (!showNewPosition) {
                  // Fetch fresh balances every time panel opens
                  fetch(`${process.env.REACT_APP_API_URL}/api/multi-balance`, {credentials: 'include'})
                    .then(r => r.json())
                    .then(d => { if (d.success) setMultiBalances(d.balances); })
                    .catch(() => {});
                }
              }}
            >
              <Plus size={18} />
              <span>Open Position</span>
              {showNewPosition ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {showNewPosition && (
              <div className="new-position-form">
                {/* Exchange Checkboxes */}
                <div className="multi-exchange-select">
                  <label>Exchanges</label>
                  <div className="exchange-checkboxes">
                    {[
                      {key: 'coindcx', name: 'CoinDCX'},
                      {key: 'hyperliquid', name: 'HL Primary'},
                      {key: 'hyperliquid_2', name: 'HL Trading'},
                      {key: 'lighter', name: 'Lighter'},
                      {key: 'aster', name: 'Aster'},
                      {key: 'blofin', name: 'BloFin'},
                    ].map(ex => (
                      <label key={ex.key} className={`exchange-checkbox ${selectedExchanges.includes(ex.key) ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedExchanges.includes(ex.key)}
                          onChange={() => {
                            setSelectedExchanges(prev =>
                              prev.includes(ex.key) ? prev.filter(e => e !== ex.key) : [...prev, ex.key]
                            );
                          }}
                        />
                        <span>{ex.name}</span>
                        {multiBalances?.[ex.key] && (
                          <span className="exch-bal">${formatNumber(multiBalances[ex.key].available)}</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Symbol & Side */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Symbol {currentPrice && <span className="current-price">${formatNumber(currentPrice)}</span>}</label>
                    <div className="symbol-search-wrapper" ref={symbolDropdownRef}>
                      <Search size={14} className="search-icon" />
                      <input
                        type="text"
                        placeholder="BTC, ETH, SOL..."
                        value={newOrder.symbol || symbolSearch}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase();
                          setSymbolSearch(val);
                          setNewOrder({...newOrder, symbol: ''});
                          setShowSymbolDropdown(true);
                          // Fetch current price
                          if (val.length >= 2) {
                            fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${val}USDT`)
                              .then(r => r.json())
                              .then(d => setCurrentPrice(parseFloat(d.price)))
                              .catch(() => {});
                          }
                        }}
                        onFocus={() => setShowSymbolDropdown(true)}
                      />
                      {showSymbolDropdown && symbolSearch && (
                        <div className="symbol-dropdown">
                          {['BTC','ETH','SOL','XRP','DOGE','ADA','AVAX','LINK','DOT','MATIC','XAU','ARB','OP','SUI','APT','NEAR','FTM','ATOM','INJ','TIA']
                            .filter(s => s.includes(symbolSearch))
                            .slice(0, 10)
                            .map(s => (
                              <div key={s} className="symbol-option" onClick={() => {
                                setNewOrder({...newOrder, symbol: s});
                                setSymbolSearch('');
                                setShowSymbolDropdown(false);
                                fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${s}USDT`)
                                  .then(r => r.json())
                                  .then(d => setCurrentPrice(parseFloat(d.price)))
                                  .catch(() => {});
                              }}>
                                <span className="symbol-name">{s}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group side-toggle">
                    <label>Side</label>
                    <div className="toggle-buttons">
                      <button type="button" className={`toggle-btn long ${newOrder.side === 'long' ? 'active' : ''}`}
                        onClick={() => setNewOrder({...newOrder, side: 'long'})}>
                        <ArrowUpCircle size={16} /> Long
                      </button>
                      <button type="button" className={`toggle-btn short ${newOrder.side === 'short' ? 'active' : ''}`}
                        onClick={() => setNewOrder({...newOrder, side: 'short'})}>
                        <ArrowDownCircle size={16} /> Short
                      </button>
                    </div>
                  </div>
                </div>

                {/* Leverage & Capital Slider */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Leverage</label>
                    <select value={newOrder.leverage} onChange={(e) => setNewOrder({...newOrder, leverage: e.target.value})}>
                      {[1, 2, 3, 5, 7, 10, 15, 20, 25, 50, 75, 100].map(lev => (
                        <option key={lev} value={lev}>{lev}x</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{flex: 2}}>
                    <label>Capital: {newOrder.marginPercent}%</label>
                    <input
                      type="range" min="0" max="100" step="1"
                      value={newOrder.marginPercent}
                      onChange={(e) => setNewOrder({...newOrder, marginPercent: parseInt(e.target.value)})}
                      className="margin-slider"
                    />
                    <div className="slider-labels">
                      {[0, 25, 33, 50, 75, 100].map(p => (
                        <span key={p} className={`slider-tick ${newOrder.marginPercent === p ? 'active' : ''}`}
                          onClick={() => setNewOrder({...newOrder, marginPercent: p})}>{p}%</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Preview Table */}
                {newOrder.symbol && newOrder.marginPercent > 0 && currentPrice > 0 && (
                  <div className="multi-preview">
                    <div className="preview-header">
                      <span>Exchange</span><span>Margin</span><span>Quantity</span>
                    </div>
                    {selectedExchanges.map(exch => {
                      const bal = multiBalances?.[exch];
                      if (!bal) return null;
                      const margin = bal.available * newOrder.marginPercent / 100;
                      const qty = (margin * parseInt(newOrder.leverage || 1)) / currentPrice;
                      return (
                        <div key={exch} className="preview-row">
                          <span className="preview-exch">{exch}</span>
                          <span>${formatNumber(margin)}</span>
                          <span>{qty.toFixed(4)} {newOrder.symbol}</span>
                        </div>
                      );
                    })}
                    <div className="preview-row preview-total">
                      <span>Total</span>
                      <span>${formatNumber(selectedExchanges.reduce((sum, e) => {
                        const b = multiBalances?.[e];
                        return sum + (b ? b.available * newOrder.marginPercent / 100 : 0);
                      }, 0))}</span>
                      <span>{selectedExchanges.reduce((sum, e) => {
                        const b = multiBalances?.[e];
                        const m = b ? b.available * newOrder.marginPercent / 100 : 0;
                        return sum + (m * parseInt(newOrder.leverage || 1)) / currentPrice;
                      }, 0).toFixed(4)} {newOrder.symbol}</span>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="button"
                  className={`btn-submit ${newOrder.side}`}
                  disabled={actionLoading === 'multi_open' || !newOrder.symbol || newOrder.marginPercent <= 0 || selectedExchanges.length === 0}
                  onClick={async () => {
                    setActionLoading('multi_open');
                    try {
                      const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/multi-create`, {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        credentials: 'include',
                        body: JSON.stringify({
                          symbol: newOrder.symbol,
                          side: newOrder.side === 'long' ? 'buy' : 'sell',
                          leverage: parseInt(newOrder.leverage),
                          margin_percent: newOrder.marginPercent,
                          exchanges: selectedExchanges,
                        }),
                      });
                      const result = await resp.json();
                      if (result.success) {
                        const ok = Object.entries(result.results).filter(([,r]) => r.success).map(([e]) => e);
                        const fail = Object.entries(result.results).filter(([,r]) => !r.success)
                          .map(([e, r]) => `${e}: ${r.error || 'unknown error'}`);
                        setMessage({type: fail.length && !ok.length ? 'error' : fail.length ? 'warning' : 'success',
                          text: ok.length ? `Opened on ${ok.join(', ')}${fail.length ? `\n\nFailed:\n${fail.join('\n')}` : ''}` : `All failed:\n${fail.join('\n')}`});
                        fetchDashboard();
                      } else {
                        setMessage({type: 'error', text: result.detail || 'Failed'});
                      }
                    } catch (err) {
                      setMessage({type: 'error', text: err.message});
                    } finally {
                      setActionLoading(null);
                    }
                  }}
                >
                  {actionLoading === 'multi_open' ? 'Opening...' : `${newOrder.side.toUpperCase()} ${newOrder.symbol || ''} / MARKET`}
                </button>
              </div>
            )}
          </div>

          <div className="exchanges-container">
            {/* CoinDCX - Admin Only (uses env vars) */}
            {userRole === 'admin' && (
            <div className="exchange-section">
              <div className="exchange-header">
                <div className="exchange-info">
                  <img
                    src="https://coindcx.com/blog/wp-content/uploads/2021/09/ProfilePic.svg"
                    alt="CoinDCX"
                    className="exchange-logo"
                  />
                  <span className="exchange-name">CoinDCX</span>
                  <span className="exchange-count">{data.coindcx.count}</span>
                </div>
                <span className={`exchange-pnl ${data.coindcx.pnl >= 0 ? 'profit' : 'loss'}`}>
                  {data.coindcx.pnl >= 0 ? '+' : ''}{formatNumber(data.coindcx.pnl)} INR
                </span>
              </div>
              {/* API Error Warning */}
              {data.coindcx.api_error && (
                <div className="api-error-banner">
                  <AlertTriangle size={16} />
                  <span>CoinDCX API temporarily unavailable. Data may be incomplete.</span>
                </div>
              )}
              <div className="positions-list">
                {data.coindcx.positions.length > 0 ? (
                  data.coindcx.positions.map((pos, idx) => (
                    <PositionRow key={idx} position={pos} exchange="coindcx" />
                  ))
                ) : (
                  <div className="no-positions">No open positions</div>
                )}
              </div>
            </div>
            )}

            {/* Hyperliquid - All Users */}
            <div className="exchange-section">
              <div className="exchange-header">
                <div className="exchange-info">
                  <img
                    src="https://www.cryptoninjas.net/wp-content/uploads/hyperliquid-logo-330x330.webp"
                    alt="Hyperliquid"
                    className="exchange-logo"
                  />
                  <span className="exchange-name">Hyperliquid</span>
                  <span className="exchange-count">{data.hyperliquid.count}</span>
                </div>
                <span className={`exchange-pnl ${data.hyperliquid.pnl >= 0 ? 'profit' : 'loss'}`}>
                  {data.hyperliquid.pnl >= 0 ? '+' : ''}{formatNumber(data.hyperliquid.pnl)} USDT
                </span>
              </div>
              {data.hyperliquid.api_error && (
                <div className="api-error-banner">
                  <AlertTriangle size={16} />
                  <span>Hyperliquid API temporarily unavailable. Data may be incomplete.</span>
                </div>
              )}
              <div className="positions-list">
                {data.hyperliquid.positions.length > 0 ? (
                  Object.entries(
                    data.hyperliquid.positions.reduce((groups, pos) => {
                      const account = pos.account_name || 'Primary';
                      if (!groups[account]) groups[account] = [];
                      groups[account].push(pos);
                      return groups;
                    }, {})
                  ).map(([accountName, positions]) => (
                    <div key={accountName} className="account-group">
                      <div className="account-header">
                        <span className="account-name">{accountName}</span>
                        <span className="account-count">{positions.length} position{positions.length !== 1 ? 's' : ''}</span>
                      </div>
                      {positions.map((pos, idx) => (
                        <PositionRow key={idx} position={pos} exchange="hyperliquid" />
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="no-positions">No open positions</div>
                )}
              </div>
            </div>

            {/* ACCESS: Admin only (Stage 1) - New exchanges */}
            {userRole === 'admin' && [
              { key: 'lighter', name: 'Lighter', logo: 'https://app.lighter.xyz/apple-touch-icon.png' },
              { key: 'aster', name: 'Aster', logo: '/aster-logo.png' },
              { key: 'blofin', name: 'BloFin', logo: '/blofin-logo.png' },
            ].map(exch => {
              const exchData = data[exch.key];
              if (!exchData || (!exchData.configured && exchData.count === 0)) return null;
              return (
                <div key={exch.key} className="exchange-section">
                  <div className="exchange-header">
                    <div className="exchange-info">
                      <img src={exch.logo} alt={exch.name} className="exchange-logo"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="exchange-name">{exch.name}</span>
                      <span className="exchange-count">{exchData.count}</span>
                    </div>
                    <span className={`exchange-pnl ${exchData.pnl >= 0 ? 'profit' : 'loss'}`}>
                      {exchData.pnl >= 0 ? '+' : ''}{formatNumber(exchData.pnl)} USDT
                    </span>
                  </div>
                  {exchData.api_error && (
                    <div className="api-error-banner">
                      <AlertTriangle size={16} />
                      <span>
                        {exchData.api_error_detail
                          ? `${exch.name}: ${exchData.api_error_detail}`
                          : `${exch.name} API temporarily unavailable. Data may be incomplete.`}
                      </span>
                    </div>
                  )}
                  <div className="positions-list">
                    {exchData.positions.length > 0 ? (
                      exchData.positions.map((pos, idx) => (
                        <PositionRow key={idx} position={pos} exchange={exch.key} />
                      ))
                    ) : (
                      <div className="no-positions">No open positions</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Close — group all positions by symbol across exchanges */}
          {(() => {
            const allPositions = [];
            ['coindcx', 'hyperliquid', 'hyperliquid_2', 'lighter', 'aster', 'blofin'].forEach(exch => {
              (data[exch]?.positions || []).forEach(p => allPositions.push({...p, exchange: exch}));
            });
            const bySymbol = {};
            allPositions.forEach(p => {
              // Normalize: B-BTC_USDT → BTC, xyz:GOLD → GOLD, BTCUSDT → BTC
              let sym = (p.symbol || '').replace('B-', '').replace('_USDT', '').replace('USDT', '').replace('USDC', '').toUpperCase();
              if (sym.includes(':')) sym = sym.split(':').pop(); // xyz:GOLD → GOLD
              if (!sym) return;
              if (!bySymbol[sym]) bySymbol[sym] = [];
              bySymbol[sym].push(p);
            });
            const allSymbols = Object.entries(bySymbol).filter(([, ps]) => ps.length >= 1);
            if (allSymbols.length === 0) return null;
            return (
              <div className="close-all-section">
                <div className="close-all-header">
                  Quick Close Across Exchanges
                  {allPositions.length > 0 && (
                    <button className="nuke-btn" onClick={() => {
                      setConfirmModal({
                        type: 'nuke',
                        symbol: 'ALL',
                        allSymbols: allSymbols,
                        totalPositions: allPositions.length,
                      });
                      setConfirmCooldown(5);
                      const timer = setInterval(() => {
                        setConfirmCooldown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
                      }, 1000);
                    }}>
                      CLOSE ALL POSITIONS
                    </button>
                  )}
                </div>
                {allSymbols.map(([sym, positions]) => (
                  <div key={sym} className="close-all-row">
                    <div className="close-all-info">
                      <span className="close-all-symbol">{sym}</span>
                      <span className="close-all-count">{positions.length} {positions.length === 1 ? 'exchange' : 'exchanges'}</span>
                      <span className={`close-all-pnl ${positions.reduce((s,p) => s + (p.pnl_usdt ?? p.pnl), 0) >= 0 ? 'profit' : 'loss'}`}>
                        {/* FIX: sum pnl_usdt (normalized) instead of mixed-currency pnl */}
                        {positions.reduce((s,p) => s + (p.pnl_usdt ?? p.pnl), 0) >= 0 ? '+' : ''}
                        ${formatNumber(positions.reduce((s,p) => s + (p.pnl_usdt ?? p.pnl), 0))}
                      </span>
                    </div>
                    <button className="close-all-btn" onClick={() => {
                      setConfirmModal({
                        type: positions.length > 1 ? 'multi-close' : 'close',
                        symbol: sym,
                        positions,
                        percent: 100,
                        // For single-exchange close, pass position details directly
                        ...(positions.length === 1 ? {
                          exchange: positions[0].exchange,
                          position: positions[0],
                        } : {}),
                      });
                      setConfirmCooldown(3);
                      const timer = setInterval(() => {
                        setConfirmCooldown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
                      }, 1000);
                    }}>
                      {positions.length > 1 ? `Close All ${sym}` : `Close ${sym}`}
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="last-update">
            <RefreshCw size={14} />
            <span>
              Auto-refresh every {Math.round(refreshInterval / 1000)}s
              {lastUpdate && ` • ${lastUpdate.toLocaleTimeString()}`}
            </span>
          </div>
        </>
        );
      })()}

      {activeTab === 'calculator' && <PnLCalculator />}
      {activeTab === 'gann' && <GannCalculator />}
      {/* ACCESS: Admin only (Stage 1) | HL Trend Bot Railway service monitor */}
      {activeTab === 'hl-trend-bot' && userRole === 'admin' && (
        <HLTrendBotPanel />
      )}
      {/* Gann V3 tab render block REMOVED 2026-05-24 — paper period expired, verdict REJECT. */}
      {/* ACCESS: Admin only (Stage 1) | ENABLED_FOR: admin | TESTED: pending */}
      {activeTab === 'r36' && userRole === 'admin' && (
        <TsmomR36Bot userRole={userRole} currentUser={currentUser} />
      )}
      {activeTab === 'vol01' && userRole === 'admin' && (
        <Vol01Panel />
      )}

      {activeTab === 'settings' && <SettingsPanel userRole={userRole} currentUser={currentUser} />}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="confirm-overlay" onClick={() => setConfirmModal(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-header">
              <AlertTriangle size={20} className="confirm-warn-icon" />
              <span>Confirm {confirmModal.type === 'close' ? 'Close Position' : 'Open Position'}</span>
            </div>
            <div className="confirm-body">
              <div className="confirm-detail">
                <span className="confirm-label">Exchange</span>
                <span className="confirm-value">{confirmModal.exchange.toUpperCase()}</span>
              </div>
              <div className="confirm-detail">
                <span className="confirm-label">Symbol</span>
                <span className="confirm-value">{confirmModal.position?.symbol}</span>
              </div>
              <div className="confirm-detail">
                <span className="confirm-label">Side</span>
                <span className={`confirm-value ${confirmModal.position?.side === 'long' ? 'profit' : 'loss'}`}>
                  {confirmModal.position?.side?.toUpperCase()}
                </span>
              </div>
              {confirmModal.type === 'close' && (
                <>
                  <div className="confirm-detail">
                    <span className="confirm-label">Close</span>
                    <span className="confirm-value">{confirmModal.percent}%</span>
                  </div>
                  <div className="confirm-detail highlight">
                    <span className="confirm-label">Quantity</span>
                    <span className="confirm-value">{formatNumber(confirmModal.quantity, 4)}</span>
                  </div>
                  <div className="confirm-detail">
                    <span className="confirm-label">Order</span>
                    <span className="confirm-value">MARKET {confirmModal.side?.toUpperCase()}</span>
                  </div>
                  <div className="confirm-detail">
                    <span className="confirm-label">Current PnL</span>
                    <span className={`confirm-value ${confirmModal.position?.pnl >= 0 ? 'profit' : 'loss'}`}>
                      {confirmModal.position?.pnl >= 0 ? '+' : ''}{formatNumber(confirmModal.position?.pnl)} {confirmModal.position?.margin_currency}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button
                className={`confirm-execute ${['close', 'multi-close', 'nuke'].includes(confirmModal.type) ? 'danger' : 'success'}`}
                onClick={confirmModal.type === 'nuke' ? async () => {
                  // NUKE: Close ALL positions across ALL symbols and exchanges
                  const { allSymbols } = confirmModal;
                  setConfirmModal(null);
                  setActionLoading('nuke');
                  let closed = 0, failed = 0;
                  try {
                    for (const [sym, positions] of allSymbols) {
                      try {
                        const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/multi-close`, {
                          method: 'POST',
                          headers: {'Content-Type': 'application/json'},
                          credentials: 'include',
                          body: JSON.stringify({
                            symbol: sym,
                            percent: 100,
                            positions: positions.map(p => ({
                              exchange: p.exchange, symbol: p.symbol, symbol_raw: p.symbol_raw,
                              side: p.side, size: p.size, margin_currency: p.margin_currency,
                            })),
                          }),
                        });
                        const result = await resp.json();
                        if (result.success) {
                          closed += Object.values(result.results).filter(r => r.success).length;
                          failed += Object.values(result.results).filter(r => !r.success).length;
                        }
                      } catch (err) {
                        failed++;
                      }
                    }
                    setMessage({type: closed > 0 ? 'success' : 'error', text: `Nuke complete: ${closed} closed, ${failed} failed`});
                    fetchDashboard();
                  } catch (err) {
                    setMessage({type: 'error', text: `Nuke error: ${err.message}`});
                  } finally {
                    setActionLoading(null);
                  }
                } : confirmModal.type === 'multi-close' ? async () => {
                  const { positions, percent } = confirmModal;
                  setConfirmModal(null);
                  setActionLoading('multi_close');
                  try {
                    const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/multi-close`, {
                      method: 'POST',
                      headers: {'Content-Type': 'application/json'},
                      credentials: 'include',
                      body: JSON.stringify({
                        symbol: confirmModal.symbol,
                        percent,
                        positions: positions.map(p => ({
                          exchange: p.exchange, symbol: p.symbol, symbol_raw: p.symbol_raw,
                          side: p.side, size: p.size, margin_currency: p.margin_currency,
                        })),
                      }),
                    });
                    const result = await resp.json();
                    if (result.success) {
                      const ok = Object.entries(result.results).filter(([,r]) => r.success).map(([e]) => e);
                      setMessage({type: 'success', text: `Closed ${confirmModal.symbol} on ${ok.join(', ')}`});
                      fetchDashboard();
                    }
                  } catch (err) {
                    setMessage({type: 'error', text: err.message});
                  } finally {
                    setActionLoading(null);
                  }
                } : executeClosePosition}
                disabled={confirmCooldown > 0}
              >
                {confirmCooldown > 0 ? `Wait ${confirmCooldown}s...`
                  : confirmModal.type === 'nuke' ? `CLOSE ALL ${confirmModal.totalPositions} POSITIONS`
                  : confirmModal.type === 'multi-close' ? `Close ${confirmModal.symbol} on ${confirmModal.positions?.length} exchanges`
                  : `Confirm ${confirmModal.type === 'close' ? 'Close' : 'Open'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
